import {
    api,
    apiErrorMessage,
    paymentSystemLabel,
    useAuthStore,
} from '@bemor/web-kit';
import {
    BodyBlock,
    Button,
    Card,
    CardBrand,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    DetailRow,
    FieldGrid,
    HttpLogDetailDrawer,
    HttpStatusBadge,
    KeyValueList,
    Money,
    ObjectRef,
    PaymentSystemIcon,
    PublicId,
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
    Skeleton,
    StatusBadge,
    statusLabel,
    statusLook,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Timeline,
    type TimelineItem,
} from '@bemor/web-kit/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type {
    Charge,
    HttpLog,
    HttpLogSummary,
    StaffCharge,
    StaffChargeHistoryEntry,
    StaffChargeRow,
} from '@/api/types';
import { DetailBody } from '@/components/detail';
import { REFUND_SOURCE_WORDS } from '@/components/refund-sheet';
import { P } from '@/lib/permissions';
import { useOne } from '@/lib/queries';
import { paymentMethodHref } from '@/pages/payment-methods/index';

/** A charge's card as staff read it: brand and last 4 (the API keeps no more). */
export const cardLabel = (card: Charge['card']) =>
    card.last4 ? `${card.brand ?? ''} •••• ${card.last4}`.trim() : '—';

/** bm-146 § 9 (D-9): the list's one 3-D Secure cell: `3DS · ECI 05 · 2.2.0` for a charge that counts, else `No 3DS`. */
export const threeDsLabel = (threeDs: StaffChargeRow['three_ds']) =>
    threeDs?.authenticated
        ? [
              '3DS',
              threeDs.eci ? `ECI ${threeDs.eci}` : null,
              threeDs.three_ds_version,
          ]
              .filter(Boolean)
              .join(' · ')
        : 'No 3DS';

/** bm-146 § 7: the provider's transaction id of a charge, whole and copyable; a dash, with no copy control, until it has one. */
/** BM-126: the quote's source as staff read it ('Source: seed by ZIP+4, v0.59.0'). */
const TAX_SOURCE: Record<string, string> = {
    provider: 'Tax provider',
    learned: "The provider's earlier answer for the ZIP",
    seed_zip4: 'Seed by ZIP+4',
    seed_zip: 'Seed by ZIP',
};
const taxSourceLabel = (source: string) => TAX_SOURCE[source] ?? source;

export function ReferenceId({ value }: { value: string | null }) {
    return value ? <PublicId id={value} short={false} /> : <>—</>;
}

/**
 * BM-51 § 7: the charge facts of a held payment, from the staff charge read (`GET /staff/charges/{charge}`). Every
 * amount is the API's, in cents; nothing is computed here.
 */
export function ChargeFacts({ chargeId }: { chargeId: string }) {
    const query = useOne<StaffCharge>(`/staff/charges/${chargeId}`);
    return (
        <DetailBody query={query} title="Charge">
            {(c) => (
                <Card data-testid="charge-facts">
                    <CardHeader>
                        <CardTitle className="text-base">Charge</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <ChargeParties charge={c} />
                        <FieldGrid>
                            <DetailRow label="Charge" layout="stacked">
                                <ObjectRef id={c.id} />
                            </DetailRow>
                            <DetailRow label="State" layout="stacked">
                                <StatusBadge status={c.state} domain="charge" />
                            </DetailRow>
                            <DetailRow label="Amount" layout="stacked">
                                <Money
                                    cents={c.amount_cents}
                                    currency={c.currency}
                                />
                            </DetailRow>
                            {/* QA-7: the order's own base and tax, labelled as the order's: a trial hold's amount is not the order total */}
                            {c.order?.subtotal_cents != null ? (
                                <DetailRow label="Order base" layout="stacked">
                                    <Money
                                        cents={c.order.subtotal_cents}
                                        currency={c.currency}
                                    />
                                </DetailRow>
                            ) : null}
                            {c.order?.tax_cents != null ? (
                                <DetailRow label="Order tax" layout="stacked">
                                    <Money
                                        cents={c.order.tax_cents}
                                        currency={c.currency}
                                    />
                                </DetailRow>
                            ) : null}
                            {/* BM-126 decision 11: which source taxed the order, with its version */}
                            {c.order?.tax_rate_source ? (
                                <div data-testid="charge-tax-source">
                                    <DetailRow
                                        label="Tax source"
                                        layout="stacked"
                                    >
                                        {taxSourceLabel(
                                            c.order.tax_rate_source,
                                        )}
                                        {c.order.tax_rate_source_version
                                            ? `, ${c.order.tax_rate_source_version}`
                                            : ''}
                                    </DetailRow>
                                </div>
                            ) : null}
                            <DetailRow label="Merchant" layout="stacked">
                                <ObjectRef id={c.merchant_id} />
                            </DetailRow>
                            {/* bm-146 § 7 (D-18): the provider's display name, with its Reference ID next to it; never the provider code */}
                            <div data-testid="charge-provider">
                                <DetailRow label="Provider" layout="stacked">
                                    {c.provider_label ?? '—'}
                                </DetailRow>
                            </div>
                            <div data-testid="charge-reference-id">
                                <DetailRow
                                    label="Reference ID"
                                    layout="stacked"
                                >
                                    <ReferenceId value={c.processor_txn_id} />
                                </DetailRow>
                            </div>
                            {/* QA-4, QA-5: no Buyer or Card row: the Customer and Payment method blocks carry them */}
                            <DetailRow label="AVS result" layout="stacked">
                                {c.avs_result ?? '—'}
                            </DetailRow>
                            <DetailRow label="CVV result" layout="stacked">
                                {c.cvv_result ?? '—'}
                            </DetailRow>
                            {/* bm-146 § 9 (D-9): authenticated or not; NMI's recorded values only when a check was recorded */}
                            <div
                                data-testid="charge-three-ds"
                                className="contents"
                            >
                                <DetailRow label="3-D Secure" layout="stacked">
                                    {c.three_ds?.authenticated
                                        ? 'Authenticated'
                                        : 'Not authenticated'}
                                </DetailRow>
                                {c.three_ds ? (
                                    <>
                                        <DetailRow label="ECI" layout="stacked">
                                            {c.three_ds.eci ?? '—'}
                                        </DetailRow>
                                        <DetailRow
                                            label="Cardholder auth"
                                            layout="stacked"
                                        >
                                            {c.three_ds.cardholder_auth ?? '—'}
                                        </DetailRow>
                                        <DetailRow
                                            label="Version"
                                            layout="stacked"
                                        >
                                            {c.three_ds.three_ds_version ?? '—'}
                                        </DetailRow>
                                    </>
                                ) : null}
                            </div>
                            <DetailRow label="Created" layout="stacked">
                                <DateTime
                                    zone="inline"
                                    value={c.created_at}
                                    empty="—"
                                />
                            </DetailRow>
                            <DetailRow label="Captured" layout="stacked">
                                <DateTime
                                    zone="inline"
                                    value={c.captured_at}
                                    empty="—"
                                />
                            </DetailRow>
                            <DetailRow label="Intent" layout="stacked">
                                {statusLabel(c.cof_intent)}
                            </DetailRow>
                        </FieldGrid>
                    </CardContent>
                </Card>
            )}
        </DetailBody>
    );
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** One stacked block of the charge facts: the label over its lines. */
function FactBlock({
    label,
    testId,
    children,
}: {
    label: string;
    testId: string;
    children: React.ReactNode;
}) {
    return (
        <div data-testid={testId} className="min-w-0">
            <DetailRow label={label} layout="stacked">
                <div className="flex min-w-0 flex-col gap-1">{children}</div>
            </DetailRow>
        </div>
    );
}

/**
 * bm-111 § 6: the session ("attempt n of N"), order, customer and payment method of the staff charge answer (§ 5),
 * one column on a phone and two from the tablet width up. A null block is omitted, never rendered empty. The ids
 * link through the app's ObjectRef mapping (`adminObjectHref`): the session and the order (bm-147 § 4) to their admin
 * pages; the customer stays unlinked until admin has a page for it (T-41).
 */
function ChargeParties({ charge: c }: { charge: StaffCharge }) {
    const pm = c.payment_method;
    if (!c.session && !c.order && !c.customer && !pm) return null;
    return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {c.session ? (
                <FactBlock label="Session" testId="charge-session">
                    <ObjectRef id={c.session.id} />
                    <span className="text-muted-foreground text-sm">
                        Attempt {c.session.attempt} of {c.session.attempts}
                    </span>
                </FactBlock>
            ) : null}
            {c.order ? (
                <FactBlock label="Order" testId="charge-order">
                    <ObjectRef id={c.order.id} />
                </FactBlock>
            ) : null}
            {c.customer ? (
                <FactBlock label="Customer" testId="charge-customer">
                    <span>{c.customer.name ?? '—'}</span>
                    <span className="text-muted-foreground text-sm break-all">
                        {c.customer.email ?? '—'}
                    </span>
                    <ObjectRef id={c.customer.id} />
                </FactBlock>
            ) : null}
            {pm ? (
                <FactBlock
                    label="Payment method"
                    testId="charge-payment-method"
                >
                    <span className="inline-flex items-center gap-2">
                        <PaymentSystemIcon system={pm.type} />
                        {paymentSystemLabel(pm.type)}
                        {pm.brand ? <CardBrand brand={pm.brand} /> : null}
                    </span>
                    {pm.last4 ? (
                        <span className="font-mono text-sm">
                            {pm.bin ?? '—'} •••• {pm.last4} ·{' '}
                            {pm.exp_month == null
                                ? '—'
                                : `${pad2(pm.exp_month)}/${pm.exp_year}`}
                        </span>
                    ) : (
                        <span
                            className="text-muted-foreground text-sm"
                            data-testid="charge-payment-method-no-card"
                        >
                            No card details returned
                        </span>
                    )}
                    <span className="text-sm">
                        {/* bm-111 § 21: a saved method links to its admin page */}
                        {pm.saved && pm.id ? (
                            <Link
                                to={paymentMethodHref(pm.id)}
                                className="hover:underline"
                                data-testid="charge-payment-method-link"
                            >
                                Saved
                            </Link>
                        ) : (
                            'One-off'
                        )}{' '}
                        · Issuer country{' '}
                        <span data-testid="charge-issuer-country">
                            {pm.issuer_country ?? '—'}
                        </span>
                    </span>
                </FactBlock>
            ) : null}
        </div>
    );
}

type NmiCall = StaffCharge['nmi_attempts'][number];

/** The NMI operations a history row's state change talked to the partner for; none for a row NMI had no part in. */
function partnerOperations(row: StaffChargeHistoryEntry): string[] {
    if (row.to === 'authorized') return ['auth'];
    if (row.to === 'approved')
        return row.from === 'authorized' || row.from === 'in_review'
            ? ['capture']
            : ['sale'];
    if (row.to === 'voided') return ['void'];
    if (row.to === 'refunded' || row.to === 'partially_refunded')
        return ['refund'];
    // the first answer of an auth or sale that did not approve
    const firstAnswer = row.from === null || row.from === 'pending';
    return firstAnswer && NOT_APPROVED.includes(row.to) ? ['auth', 'sale'] : [];
}

const NOT_APPROVED = ['declined', 'error', 'invalid', 'unknown'];

const callTime = (a: NmiCall) =>
    Date.parse(a.completed_at ?? a.created_at ?? '');

/**
 * bm-111 QA-8: each partner event of the history gets the NMI call that made it: of the charge's calls with a matching
 * operation, the one completed nearest the row (the call and its state change are written in one transaction). Calls
 * no row took (a failed refund or capture moves no state) are returned apart, so none is hidden.
 */
function matchCalls(
    rows: readonly StaffChargeHistoryEntry[],
    calls: readonly NmiCall[],
) {
    const free = [...calls];
    const byRow = rows.map((row) => {
        const ops = partnerOperations(row);
        const at = Date.parse(row.at);
        let best = -1;
        free.forEach((c, i) => {
            if (!ops.includes(c.operation)) return;
            if (
                best < 0 ||
                Math.abs(callTime(c) - at) <
                    Math.abs(callTime(free[best]!) - at)
            )
                best = i;
        });
        return best < 0 ? null : free.splice(best, 1)[0]!;
    });
    return { byRow, rest: free };
}

const chargeStateLabel = (state: string) => statusLook(state, 'charge')[1];

/**
 * The charge's logged HTTP calls (`GET /staff/http-logs?charge_id[]=`, `platform.http_logs.read`, B-15), newest
 * first through the cursor: the HTTP calls list and the partner call sheet read the same cached pages.
 */
function useChargeHttpLogs(chargeId: string, enabled: boolean) {
    return useInfiniteQuery({
        queryKey: ['/staff/http-logs', 'charge', chargeId],
        queryFn: ({ pageParam }) =>
            api().list<HttpLogSummary>('/staff/http-logs', {
                params: { charge_id: [chargeId], cursor: pageParam },
            }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (page) => page.nextCursor ?? undefined,
        enabled,
    });
}

/** The operation an NMI v5 call's URL names: its last path segment (`payments/auth`, `payments/{id}/capture`). */
const urlOperation = (url: string) => url.split('?')[0]!.split('/').at(-1);

/**
 * bm-111 QA round 8: the http_logs row of each partner call, by its order id: of the charge's outbound NMI logs (the
 * attempts are pay_nmi_charge_attempts rows, so NMI's by definition) naming the same operation, the one logged nearest
 * the call's answer. A call with no such row (made before calls carried the charge id) has none.
 * ponytail: matches the loaded log pages only; a charge has a handful of calls, far under one page.
 */
function matchLogs(calls: readonly NmiCall[], logs: readonly HttpLogSummary[]) {
    const free = logs.filter(
        (l) => l.client === 'nmi' && l.direction === 'out',
    );
    const byCall = new Map<string, HttpLogSummary>();
    for (const call of calls) {
        const at = callTime(call);
        const gap = (l: HttpLogSummary) =>
            Math.abs(Date.parse(l.created_at) - at);
        let best = -1;
        free.forEach((l, i) => {
            if (urlOperation(l.url) !== call.operation) return;
            if (best < 0 || gap(l) < gap(free[best]!)) best = i;
        });
        if (best >= 0) byCall.set(call.order_id, free.splice(best, 1)[0]!);
    }
    return byCall;
}

/** The button that opens one partner call in place. */
function ViewCallButton({
    call,
    onOpen,
}: {
    call: NmiCall;
    onOpen: (c: NmiCall) => void;
}) {
    return (
        <Button
            size="sm"
            variant="outline"
            className="mt-1"
            aria-label={`View API ${call.operation} call ${call.order_id}`}
            onClick={() => onOpen(call)}
        >
            View API call
        </Button>
    );
}

/**
 * `phase` places a step inside its second (QA-35): Risk check and NMI attempt times are whole seconds while history
 * rows keep microseconds, so time alone sorts a post_auth check before the authorization it follows.
 */
type Step = TimelineItem & {
    order: number;
    phase?: 'pre_auth' | 'post_auth' | 'authorization';
};

/**
 * bm-111 QA-35 (§ 6): time order, with same-second steps ordered causally: a pre_auth check before every step of its
 * second, a post_auth check right after the latest authorization (the authorized row or its partner call) of its
 * second. Display only; the stored times are untouched.
 */
function causalOrder(steps: Step[]): Step[] {
    const second = (t: number) => Math.floor(t / 1000);
    const authAt = new Map<number, number>();
    for (const s of steps) {
        if (s.phase !== 'authorization') continue;
        const t = Date.parse(s.at);
        authAt.set(second(t), Math.max(authAt.get(second(t)) ?? t, t));
    }
    const key = (s: Step): [number, number] => {
        const t = Date.parse(s.at);
        if (s.phase === 'pre_auth') return [second(t) * 1000, -1];
        const auth = authAt.get(second(t));
        if (s.phase === 'post_auth' && auth !== undefined)
            return [Math.max(t, auth), 1];
        return [t, 0];
    };
    return steps
        .map((s) => ({ s, k: key(s) }))
        .sort(
            (a, b) =>
                a.k[0] - b.k[0] || a.k[1] - b.k[1] || a.s.order - b.s.order,
        )
        .map(({ s }) => s);
}

/** A step that is the authorization of the charge: an approved auth or sale. */
const isAuthorization = (row: StaffChargeHistoryEntry) =>
    row.to === 'authorized' ||
    (row.to === 'approved' && partnerOperations(row).includes('sale'));

/**
 * bm-111 QA round 8: the steps of the staff charge answer that are not state changes: its attempt's Risk checks, the
 * tax steps, each refund and the inbound webhooks, each with its time, result and detail. A review read of the charge
 * that predates a field carries none of it.
 */
function chargeSteps(c: StaffCharge | undefined, from: number): Step[] {
    if (!c) return [];
    const steps: Omit<Step, 'order'>[] = [
        ...(c.risk_checks ?? []).map((r) => ({
            id: `risk-${r.id}`,
            at: r.created_at,
            phase:
                r.stage === 'pre_auth' || r.stage === 'post_auth'
                    ? r.stage
                    : undefined,
            tone:
                r.verdict === 'decline'
                    ? ('danger' as const)
                    : r.verdict === 'review'
                      ? ('warning' as const)
                      : undefined,
            title: `Risk check (${statusLabel(r.stage)})`,
            actor: 'Risk',
            detail: (
                <>
                    {statusLabel(r.verdict)} · score {r.score}
                    <div className="text-fg-2">
                        {r.rules_hit.length
                            ? `Rules fired: ${r.rules_hit.join(', ')}`
                            : 'No rule fired'}
                    </div>
                </>
            ),
        })),
        ...(c.tax_transactions ?? []).map((t) => ({
            id: `tax-${t.id}`,
            at: t.committed_at ?? t.created_at,
            tone: t.state === 'failed' ? ('danger' as const) : undefined,
            title: `Tax ${statusLabel(t.kind).toLowerCase()}`,
            actor: statusLabel(t.provider),
            detail: (
                <>
                    {statusLabel(t.state)} ·{' '}
                    <Money cents={t.tax_cents} currency={c.currency} />
                    {/* QA-33 (T-58): the committed tax, and what the order collected for this step when the provider committed another figure */}
                    {t.collected_tax_cents != null &&
                    t.collected_tax_cents !== t.tax_cents ? (
                        <>
                            {' · collected '}
                            <Money
                                cents={t.collected_tax_cents}
                                currency={c.currency}
                            />
                        </>
                    ) : null}
                    {t.error_code ? (
                        <div className="text-fg-2">{t.error_code}</div>
                    ) : null}
                </>
            ),
        })),
        ...(c.refunds ?? []).map((r) => ({
            id: `refund-${r.id}`,
            at: r.created_at,
            tone:
                r.state === 'failed' || r.state === 'rejected'
                    ? ('danger' as const)
                    : undefined,
            title: 'Refund',
            actor: REFUND_SOURCE_WORDS[r.source],
            detail: (
                <>
                    <Money cents={r.amount_cents} currency={r.currency} /> ·{' '}
                    {statusLabel(r.state)}
                    {r.reason ? <div>{statusLabel(r.reason)}</div> : null}
                    {/* bm-147 B-9: the issuer and approver as the refunds list names them; an unresolved id shows as written */}
                    <div>
                        Initiated by {r.initiated_by_name ?? r.initiated_by}
                        {r.initiated_by_role ? ` (${r.initiated_by_role})` : ''}
                    </div>
                    {r.decided_by === null ? null : (
                        <div>
                            Approved by {r.decided_by_name ?? r.decided_by}
                        </div>
                    )}
                    {r.failure_reason ? (
                        <div className="text-fg-2">{r.failure_reason}</div>
                    ) : null}
                </>
            ),
        })),
        ...(c.inbound_webhooks ?? []).map((w, i) => ({
            id: `webhook-${i}`,
            at: w.received_at,
            tone: w.status === 'failed' ? ('danger' as const) : undefined,
            title: `Webhook ${w.event_type}`,
            actor: 'Inbound webhook',
            detail: (
                <>
                    {statusLabel(w.status)}
                    {w.error ? (
                        <div className="text-fg-2">{w.error}</div>
                    ) : null}
                </>
            ),
        })),
    ];
    return steps.map((s, i) => ({ ...s, order: from + i }));
}

/**
 * The staff payment timeline (`GET /staff/charges/{charge}/history`, oldest first): every row with the staff
 * member's id and note. A charge with no history row shows the timeline's own empty text. bm-111 QA-8: every event that
 * talked to NMI (auth or sale, capture, void, refund) opens that call in place; a call no state change took is its
 * own event. QA round 8: the Risk checks, tax steps, refunds and inbound webhooks of the charge are events too.
 */
export function ChargeTimeline({ chargeId }: { chargeId: string }) {
    const query = useOne<StaffChargeHistoryEntry[]>(
        `/staff/charges/${chargeId}/history`,
    );
    const charge = useOne<StaffCharge>(`/staff/charges/${chargeId}`);
    const logsReadable = useAuthStore((s) => s.can)(P.httpLogsRead);
    const logs = useChargeHttpLogs(chargeId, logsReadable);
    const [open, setOpen] = useState<NmiCall | null>(null);
    // a review read of the charge from before QA-8 carries no calls
    const calls = charge.data?.data.nmi_attempts ?? [];
    const logOf = matchLogs(
        calls,
        logs.data?.pages.flatMap((p) => p.items) ?? [],
    );
    return (
        <DetailBody query={query} title="Payment timeline">
            {(rows) => {
                const { byRow, rest } = matchCalls(rows, calls);
                const items: Step[] = causalOrder([
                    ...rows.map((row, i) => {
                        const actor = statusLabel(row.actor);
                        const call = byRow[i];
                        return {
                            id: `row-${i}`,
                            order: i,
                            at: row.at,
                            phase: isAuthorization(row)
                                ? ('authorization' as const)
                                : undefined,
                            title: row.from
                                ? `${chargeStateLabel(row.from)} → ${chargeStateLabel(row.to)}`
                                : chargeStateLabel(row.to),
                            actor: row.actor_id
                                ? `${actor} · ${row.actor_id}`
                                : actor,
                            detail: (
                                <>
                                    {statusLabel(row.reason)}
                                    {row.note ? (
                                        <div className="text-fg-2">
                                            {row.note}
                                        </div>
                                    ) : null}
                                    {call ? (
                                        <ViewCallButton
                                            call={call}
                                            onOpen={setOpen}
                                        />
                                    ) : null}
                                </>
                            ),
                        };
                    }),
                    ...rest.map((call, i) => ({
                        id: `call-${call.order_id}`,
                        order: rows.length + i,
                        at: call.completed_at ?? call.created_at ?? '',
                        phase:
                            call.state === 'approved' &&
                            (call.operation === 'auth' ||
                                call.operation === 'sale')
                                ? ('authorization' as const)
                                : undefined,
                        tone:
                            call.state === 'approved'
                                ? undefined
                                : ('danger' as const),
                        title: `API ${call.operation} call`,
                        actor: 'Partner API',
                        detail: (
                            <>
                                {statusLabel(call.state)}
                                {call.response_text ? (
                                    <div className="text-fg-2">
                                        {call.response_text}
                                    </div>
                                ) : null}
                                <ViewCallButton call={call} onOpen={setOpen} />
                            </>
                        ),
                    })),
                    ...chargeSteps(
                        charge.data?.data,
                        rows.length + rest.length,
                    ),
                ]);
                return (
                    <Card data-testid="charge-timeline">
                        <CardHeader>
                            <CardTitle className="text-base">
                                Payment timeline
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Timeline items={items} />
                            <NmiCallSheet
                                call={open}
                                exchange={
                                    logsReadable
                                        ? {
                                              log: open
                                                  ? (logOf.get(open.order_id) ??
                                                    null)
                                                  : null,
                                              pending: logs.isPending,
                                              error: logs.error,
                                          }
                                        : null
                                }
                                currency={charge.data?.data.currency}
                                onClose={() => setOpen(null)}
                            />
                        </CardContent>
                    </Card>
                );
            }}
        </DetailBody>
    );
}

/**
 * One partner call in full (QA-8): its outcome on top, then, for staff who may read HTTP logs (T-25), the real
 * exchange (QA round 8): the http_logs row of the call with its headers and bodies as the HTTP log drawer shows them,
 * or, when no row matched, the request and response summary stored with the call, said to be that. Without the
 * permission (`exchange` null) the outcome only.
 */
function NmiCallSheet({
    call,
    exchange,
    currency,
    onClose,
}: {
    call: NmiCall | null;
    exchange: {
        log: HttpLogSummary | null;
        pending: boolean;
        error: Error | null;
    } | null;
    currency: string | undefined;
    onClose: () => void;
}) {
    return (
        <Sheet
            open={call !== null}
            onOpenChange={(o) => (o ? null : onClose())}
        >
            <SheetContent
                side="right"
                className="w-full overflow-y-auto p-0 sm:max-w-2xl"
                data-testid="nmi-call-sheet"
            >
                <SheetTitle className="sr-only">API call</SheetTitle>
                <SheetDescription className="sr-only">
                    The outcome of one partner API call, and its request and
                    response, secrets masked
                </SheetDescription>
                {call ? (
                    <div className="flex flex-col gap-5 p-5">
                        <h2 className="text-fg-1 text-20 m-0 font-semibold">
                            API {call.operation} call
                        </h2>
                        <KeyValueList
                            items={[
                                {
                                    label: 'State',
                                    value: statusLabel(call.state),
                                },
                                {
                                    label: 'Amount',
                                    value: (
                                        <Money
                                            cents={call.amount_cents}
                                            currency={currency}
                                        />
                                    ),
                                },
                                {
                                    label: 'Response code',
                                    value: call.response_code ?? '—',
                                    mono: true,
                                },
                                {
                                    label: 'Response text',
                                    value: call.response_text ?? '—',
                                },
                                {
                                    label: 'AVS',
                                    value: call.avs_result ?? '—',
                                    mono: true,
                                },
                                {
                                    label: 'CVV',
                                    value: call.cvv_result ?? '—',
                                    mono: true,
                                },
                                {
                                    label: 'Transaction id',
                                    value: call.nmi_txn_id ?? '—',
                                    mono: true,
                                },
                                call.error_code
                                    ? {
                                          label: 'Error',
                                          value: call.error_code,
                                          mono: true,
                                      }
                                    : null,
                                {
                                    label: 'Order id',
                                    value: call.order_id,
                                    mono: true,
                                },
                                {
                                    label: 'Duration',
                                    value:
                                        call.latency_ms === null
                                            ? '—'
                                            : `${call.latency_ms} ms`,
                                },
                                {
                                    label: 'Time',
                                    value: (
                                        <DateTime
                                            zone="inline"
                                            value={
                                                call.completed_at ??
                                                call.created_at
                                            }
                                            empty="—"
                                        />
                                    ),
                                },
                            ]}
                        />
                        {exchange === null ? null : exchange.error ? (
                            <p
                                role="alert"
                                className="text-destructive m-0 text-sm"
                            >
                                {apiErrorMessage(exchange.error)}
                            </p>
                        ) : exchange.pending ? (
                            <Skeleton className="h-24 w-full" />
                        ) : exchange.log ? (
                            <LoggedExchange logId={exchange.log.id} />
                        ) : (
                            <>
                                <p
                                    className="text-fg-2 m-0 text-sm"
                                    data-testid="call-stored-summary"
                                >
                                    No HTTP log matched this call, so this is
                                    the summary of its request and response
                                    stored with the call, card data redacted.
                                </p>
                                <BodyBlock
                                    label="Request (stored summary)"
                                    body={call.request}
                                />
                                <BodyBlock
                                    label="Response (stored summary)"
                                    body={call.response}
                                />
                            </>
                        )}
                    </div>
                ) : null}
            </SheetContent>
        </Sheet>
    );
}

/** The logged exchange of one call (`GET /staff/http-logs/{id}`): the blocks of the HTTP log drawer. */
function LoggedExchange({ logId }: { logId: string }) {
    const detail = useOne<HttpLog>(`/staff/http-logs/${logId}`);
    const log = detail.data?.data;
    if (detail.isError)
        return (
            <p role="alert" className="text-destructive m-0 text-sm">
                {apiErrorMessage(detail.error)}
            </p>
        );
    if (!log) return <Skeleton className="h-24 w-full" />;
    return (
        <div className="flex flex-col gap-5" data-testid="call-http-log">
            <p className="text-fg-2 m-0 text-sm">
                The call as logged; secrets were masked when it was logged.
            </p>
            <KeyValueList
                items={[
                    { label: 'Client', value: log.client },
                    {
                        label: 'Request',
                        value: (
                            <span className="font-mono text-xs break-all">
                                {log.method} {log.url}
                            </span>
                        ),
                    },
                    {
                        label: 'Status',
                        value: <HttpStatusBadge status={log.status} />,
                    },
                ]}
            />
            <BodyBlock label="Request headers" body={log.request_headers} />
            <BodyBlock label="Request body" body={log.request_body} />
            <BodyBlock label="Response headers" body={log.response_headers} />
            <BodyBlock label="Response body" body={log.response_body} />
        </div>
    );
}

type InboundWebhook = StaffCharge['inbound_webhooks'][number];

/** One row of the HTTP calls list: a logged call (its log opens in place) or an inbound NMI webhook (its payload). */
type HttpCall = {
    key: string;
    at: string;
    client: string;
    request: string;
    status: React.ReactNode;
    duration: number | null;
    error: string | null;
    failed: boolean;
    logId: string | null;
    webhook: InboundWebhook | null;
};

const fromLog = (r: HttpLogSummary): HttpCall => ({
    key: r.id,
    at: r.created_at,
    client: r.client,
    request: `${r.method} ${r.url}`,
    status: <HttpStatusBadge status={r.status} />,
    duration: r.duration_ms,
    error: r.error,
    // bm-111 § 4: 4xx/5xx, an error, or no answer at all
    failed: r.status === null || r.status >= 400 || r.error !== null,
    logId: r.id,
    webhook: null,
});

const fromWebhook = (w: InboundWebhook, i: number): HttpCall => ({
    key: `webhook-${i}`,
    at: w.received_at,
    client: 'nmi webhook (in)',
    request: w.event_type,
    status: statusLabel(w.status),
    duration: null,
    error: w.error,
    failed: w.status === 'failed',
    logId: null,
    webhook: w,
});

/** bm-111 QA-2: one inbound NMI webhook in full: its outcome and its stored body, masked, pretty-printed. */
function WebhookSheet({
    webhook,
    onClose,
}: {
    webhook: InboundWebhook | null;
    onClose: () => void;
}) {
    return (
        <Sheet
            open={webhook !== null}
            onOpenChange={(o) => (o ? null : onClose())}
        >
            <SheetContent
                side="right"
                className="w-full overflow-y-auto p-0 sm:max-w-2xl"
                data-testid="webhook-sheet"
            >
                <SheetTitle className="sr-only">Inbound webhook</SheetTitle>
                <SheetDescription className="sr-only">
                    The body NMI sent, card data masked
                </SheetDescription>
                {webhook ? (
                    <div className="flex flex-col gap-5 p-5">
                        <div>
                            <h2 className="text-fg-1 text-20 m-0 font-semibold">
                                Inbound NMI webhook
                            </h2>
                            <p className="text-fg-2 mt-1 mb-0 text-sm">
                                Card data was masked before it was shown.
                            </p>
                        </div>
                        <KeyValueList
                            items={[
                                {
                                    label: 'Event',
                                    value: webhook.event_type,
                                    mono: true,
                                },
                                {
                                    label: 'Status',
                                    value: statusLabel(webhook.status),
                                },
                                {
                                    label: 'Received',
                                    value: (
                                        <DateTime
                                            zone="inline"
                                            value={webhook.received_at}
                                        />
                                    ),
                                },
                                webhook.error
                                    ? { label: 'Error', value: webhook.error }
                                    : null,
                            ]}
                        />
                        <BodyBlock label="Payload" body={webhook.payload} />
                    </div>
                ) : null}
            </SheetContent>
        </Sheet>
    );
}

/**
 * bm-111 § 4: every HTTP call the charge caused, from the staff HTTP log list filtered by the charge
 * (`GET /staff/http-logs?charge_id[]=`, `platform.http_logs.read`, B-15) merged newest first with the inbound NMI
 * webhooks of the staff charge answer (B-16). Hidden, with no call made, without that permission.
 */
export function ChargeHttpCalls({ chargeId }: { chargeId: string }) {
    const readable = useAuthStore((s) => s.can)(P.httpLogsRead);
    const charge = useOne<StaffCharge>(
        readable ? `/staff/charges/${chargeId}` : null,
    );
    const logs = useChargeHttpLogs(chargeId, readable);
    // QA-2: a row opens in place, as on the HTTP logs page: a logged call's full log, a webhook's payload
    const [logId, setLogId] = useState<string | null>(null);
    const [webhook, setWebhook] = useState<InboundWebhook | null>(null);
    const detail = useOne<HttpLog>(
        readable && logId ? `/staff/http-logs/${logId}` : null,
    );
    const openRow = (r: HttpCall) =>
        r.logId ? setLogId(r.logId) : setWebhook(r.webhook);
    if (!readable) return null;
    // ponytail: webhooks older than the loaded log page show before "Load more"; a merged cursor would need an api route
    const rows = [
        ...(logs.data?.pages.flatMap((p) => p.items) ?? []).map(fromLog),
        ...(charge.data?.data.inbound_webhooks ?? []).map(fromWebhook),
    ].sort((a, b) => b.at.localeCompare(a.at));
    const error = logs.error ?? charge.error;
    return (
        <Card data-testid="charge-http-calls">
            <CardHeader>
                <CardTitle className="text-base">HTTP calls</CardTitle>
            </CardHeader>
            <CardContent>
                {error ? (
                    <p role="alert" className="text-destructive text-sm">
                        {apiErrorMessage(error)}
                    </p>
                ) : logs.isPending || charge.isPending ? (
                    <Skeleton className="h-24 w-full" />
                ) : rows.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                        No HTTP calls were logged for this charge.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Time</TableHead>
                                    <TableHead>Client</TableHead>
                                    <TableHead>Request</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Duration</TableHead>
                                    <TableHead>Error</TableHead>
                                    <TableHead>
                                        <span className="sr-only">Detail</span>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow
                                        key={r.key}
                                        data-testid="charge-http-call"
                                        data-failed={String(r.failed)}
                                        className={
                                            r.failed
                                                ? 'bg-destructive/5 cursor-pointer'
                                                : 'cursor-pointer'
                                        }
                                        onClick={() => openRow(r)}
                                    >
                                        <TableCell className="whitespace-nowrap">
                                            <DateTime value={r.at} />
                                        </TableCell>
                                        <TableCell>{r.client}</TableCell>
                                        <TableCell className="font-mono text-xs break-all">
                                            {r.request}
                                        </TableCell>
                                        <TableCell>
                                            {r.failed ? (
                                                <span className="text-destructive mr-2 text-xs font-semibold">
                                                    Failed
                                                </span>
                                            ) : null}
                                            {r.status}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap tabular-nums">
                                            {r.duration === null
                                                ? '—'
                                                : `${r.duration} ms`}
                                        </TableCell>
                                        <TableCell className="text-destructive text-xs">
                                            {r.error ?? '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                aria-label={
                                                    r.logId
                                                        ? `View call ${r.logId}`
                                                        : `View webhook ${r.request}`
                                                }
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openRow(r);
                                                }}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
                {logs.hasNextPage ? (
                    <Button
                        variant="outline"
                        className="mt-3"
                        disabled={logs.isFetchingNextPage}
                        onClick={() => void logs.fetchNextPage()}
                    >
                        Load more
                    </Button>
                ) : null}
                <HttpLogDetailDrawer
                    open={logId !== null}
                    onOpenChange={(o) => (o ? null : setLogId(null))}
                    log={detail.data?.data ?? null}
                    loading={detail.isPending && logId !== null}
                    error={
                        detail.isError ? apiErrorMessage(detail.error) : null
                    }
                />
                <WebhookSheet
                    webhook={webhook}
                    onClose={() => setWebhook(null)}
                />
            </CardContent>
        </Card>
    );
}
